/**
 * Finding the other legs of a package.
 *
 * Legs of one package cross together — same underlying, same exchange, same exchange print
 * time to the millisecond — so the tape states which prints belong together. Nothing here
 * ranks prints by resemblance or asks anyone to link them by hand.
 *
 * Two steps, because a contract prints many times a day and only some of those instants are
 * packages:
 *
 *   1. `fetchLegAnchors` — the instants this contract printed at, packages FIRST. A
 *      contract's biggest prints are not its package prints: SPXW 7700C's five largest were
 *      all single-leg, while the instant carrying a vertical was a 100-lot.
 *   2. `fetchLegSurvey` — every leg that printed at the chosen instant. Omit `expiry` and
 *      calendars and diagonals come back too, since those are packages whose legs sit in
 *      different expiries.
 */

import { getApiUrl } from '../config/api';
import { getMmecAuthHeaderFields } from '../utils/authUtils';

export interface LegAnchor {
  msOfDay: number;
  exchange: number;
  contracts: number;
  premium: number;
  prints: number;
  condition: number;
  /** The exchange flagged this instant as part of a package. */
  multiLeg: boolean;
}

export interface SurveyLeg {
  expiry: string;
  strike: number;
  right: string;
  side: string;
  contracts: number;
  premium: number;
  prints: number;
  condition: number;
}

export interface SurveyPackage {
  /** True only when the exchange said so. A co-print is never called a package here. */
  confirmedByExchange: boolean;
  conditionNames: string[];
  legCount: number;
  spansExpiries: boolean;
  expiries: string[];
  totalPremium: number;
}

export interface LegSurvey {
  legs: SurveyLeg[];
  package: SurveyPackage;
}

export interface LegAnchorQuery {
  underlying: string;
  expiry: string;
  strike: number;
  right: string;
  sessionYmd: string;
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
  });

/**
 * Fetch with auth + tab headers. A 429 here is the session gate (per-login tab/
 * concurrency cap), not a hard error — back off on Retry-After and retry a few
 * times so a transient burst self-heals instead of surfacing "HTTP 429".
 */
async function authFetch(url: string, signal?: AbortSignal): Promise<Response> {
  const maxRetries = 3;
  for (let attempt = 0; ; attempt++) {
    const res = await fetch(url, { signal, headers: { ...getMmecAuthHeaderFields() } });
    if (res.status !== 429 || attempt >= maxRetries) return res;
    const ra = Number(res.headers.get('Retry-After'));
    const backoff = Number.isFinite(ra) && ra > 0 ? ra * 1000 : Math.min(4000, 500 * 2 ** attempt);
    await sleep(backoff, signal);
  }
}

/** `YYYY-MM-DD` or `YYYYMMDD` to the compact form the tape is keyed by. */
export function toSessionYmd(value: string): string {
  return String(value || '').replace(/[^0-9]/g, '').slice(0, 8);
}

export async function fetchLegAnchors(
  q: LegAnchorQuery,
  signal?: AbortSignal,
): Promise<LegAnchor[]> {
  const ymd = toSessionYmd(q.sessionYmd);
  if (!q.underlying || !q.expiry || !ymd || !(q.strike > 0)) return [];
  const u = new URL(getApiUrl('/api/flow/leg-anchors'), window.location.origin);
  u.searchParams.set('ticker', q.underlying);
  u.searchParams.set('expiry', q.expiry);
  u.searchParams.set('strike', String(q.strike));
  u.searchParams.set('right', q.right);
  u.searchParams.set('date', ymd);
  u.searchParams.set('limit', '25');
  const res = await authFetch(u.toString(), signal);
  if (!res.ok) throw new Error(`leg anchors HTTP ${res.status}`);
  const json = await res.json();
  const rows = Array.isArray(json?.anchors) ? json.anchors : [];
  return rows.map((a: Record<string, unknown>) => ({
    msOfDay: Number(a.ms_of_day) || 0,
    exchange: Number(a.exchange) || 0,
    contracts: Number(a.contracts) || 0,
    premium: Number(a.premium) || 0,
    prints: Number(a.prints) || 0,
    condition: Number(a.condition) || 0,
    multiLeg: Boolean(a.multi_leg),
  }));
}

export async function fetchLegSurvey(
  args: { underlying: string; sessionYmd: string; msOfDay: number; exchange: number; expiry?: string },
  signal?: AbortSignal,
): Promise<LegSurvey | null> {
  const ymd = toSessionYmd(args.sessionYmd);
  if (!args.underlying || !ymd || !args.msOfDay) return null;
  const u = new URL(getApiUrl('/api/flow/leg-survey'), window.location.origin);
  u.searchParams.set('ticker', args.underlying);
  u.searchParams.set('date', ymd);
  u.searchParams.set('ms', String(args.msOfDay));
  u.searchParams.set('exchange', String(args.exchange));
  // Only when the caller explicitly wants one expiry. Left off, a calendar survives.
  if (args.expiry) u.searchParams.set('expiry', args.expiry);
  const res = await authFetch(u.toString(), signal);
  if (!res.ok) throw new Error(`leg survey HTTP ${res.status}`);
  const json = await res.json();
  const legs = Array.isArray(json?.legs) ? json.legs : [];
  const pkg = json?.package || {};
  return {
    legs: legs.map((l: Record<string, unknown>) => ({
      expiry: String(l.expiry || ''),
      strike: Number(l.strike) || 0,
      right: String(l.right || ''),
      side: String(l.side || ''),
      contracts: Number(l.contracts) || 0,
      premium: Number(l.premium) || 0,
      prints: Number(l.prints) || 0,
      condition: Number(l.condition) || 0,
    })),
    package: {
      confirmedByExchange: Boolean(pkg.confirmed_by_exchange),
      conditionNames: Array.isArray(pkg.condition_names) ? pkg.condition_names.map(String) : [],
      legCount: Number(pkg.leg_count) || legs.length,
      spansExpiries: Boolean(pkg.spans_expiries),
      expiries: Array.isArray(pkg.expiries) ? pkg.expiries.map(String) : [],
      totalPremium: Number(pkg.total_premium) || 0,
    },
  };
}

/** Milliseconds past midnight ET, as a readable clock. */
export function msOfDayLabel(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '--:--:--';
  const s = Math.floor(ms / 1000);
  const hh = String(Math.floor(s / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}:${ss}.${String(ms % 1000).padStart(3, '0')}`;
}
